import React, { useState } from 'react';
import { Anchor, Box, Button, Group, Modal, Text, useMantineColorScheme } from '@mantine/core';
import { IconAlertTriangle, IconRadar2, IconExternalLink } from '@tabler/icons-react';

interface SmartCodeNotDetectedModalProps {
  opened: boolean;
  pageUrl?: string;
  pageUrls?: string[];
  onClose: () => void;
  // Resolve true when the page was added despite the missing SmartCode
  onContinueAnyway: () => Promise<boolean>;
  // Resolve true when the SmartCode is found on a re-check
  onRecheck?: () => Promise<boolean>;
  // Opens the in-app VisiSmart Code install modal for this page
  onViewInstructions?: () => void;
  guideUrl?: string;
}

// Shown when the VisiSmart Code can't be detected on a page the user is adding
// to a funnel. Lets them re-check or continue anyway (dynamic / cached sites).
export function SmartCodeNotDetectedModal({
  opened,
  pageUrl,
  pageUrls,
  onClose,
  onContinueAnyway,
  onRecheck,
  onViewInstructions,
  guideUrl,
}: SmartCodeNotDetectedModalProps) {
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';
  const cTitle = dark ? '#F3F4F6' : '#111827';
  const cBody = dark ? '#C4C9D2' : '#4B5563';
  const cPurple = dark ? '#A78BFA' : '#6D28D9';
  const surfaceBg = dark ? '#15181E' : '#F9FAFB';
  const surfaceBorder = dark ? '#2A2E37' : '#EAEAEA';
  const amberBg = dark ? '#2A2410' : '#FFFBEB';
  const amberBorder = dark ? '#4D3B12' : '#FDE68A';
  const amberText = dark ? '#FCD34D' : '#92400E';
  // One or many pages — normalize to a list for rendering
  const urls = pageUrls && pageUrls.length ? pageUrls : pageUrl ? [pageUrl] : [];
  const many = urls.length > 1;
  const [checking, setChecking] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const [notFoundAgain, setNotFoundAgain] = useState(false);

  const handleRecheck = () => {
    if (!onRecheck) return;
    setChecking(true);
    setNotFoundAgain(false);
    onRecheck()
      .then((found) => {
        setChecking(false);
        if (!found) setNotFoundAgain(true);
      })
      .catch(() => setChecking(false));
  };

  const handleContinue = () => {
    setContinuing(true);
    Promise.resolve(onContinueAnyway())
      .then(() => setContinuing(false))
      .catch(() => setContinuing(false));
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      // centered
      radius="lg"
      size={560}
      zIndex={10000}
      title={
        <Group spacing={10}>
          <Box
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: '#FFFBEB',
              color: '#B45309',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <IconAlertTriangle size={19} />
          </Box>
          <Text fw={700} fz="md" c={cTitle}>
            SmartCode Not Detected Yet
          </Text>
        </Group>
      }
    >
      {urls.length > 0 && (
        <Box
          style={{
            background: surfaceBg,
            border: `1px solid ${surfaceBorder}`,
            borderRadius: 10,
            padding: '9px 12px',
            marginBottom: 14,
          }}
        >
          <Text fz={11.5} fw={700} c="dimmed" style={{ letterSpacing: '0.04em' }}>
            {many ? `PAGES (${urls.length})` : 'PAGE'}
          </Text>
          <Box style={{ display: 'grid', gap: 4, maxHeight: 140, overflowY: 'auto' }}>
            {urls.map((u) => (
              <Text
                key={u}
                fz={13}
                c={cTitle}
                style={{ fontFamily: 'ui-monospace, Menlo, monospace', wordBreak: 'break-all' }}
              >
                {u}
              </Text>
            ))}
          </Box>
        </Box>
      )}

      <Text fz={14} c={cBody} lh={1.6}>
        {many
          ? "We weren't able to automatically detect the Visiopt SmartCode on these pages."
          : "We weren't able to automatically detect the Visiopt SmartCode on this page."}
      </Text>
      <Text fz={14} c={cBody} lh={1.6} mt={10}>
        If your website loads the SmartCode dynamically after the page has already loaded, Visiopt may
        not be able to automatically detect it even though it's installed correctly. In these cases, you
        can safely click <b>Continue Anyway</b>.
      </Text>
      <Text fz={14} c={cBody} lh={1.6} mt={10}>
        Otherwise, if you've already installed the SmartCode, wait a few moments and click{' '}
        <b>Re-check</b>.
      </Text>
      <Text fz={14} c={cBody} lh={1.6} mt={10}>
        If the SmartCode still isn't detected and your website does not use a dynamic setup, review the
        installation instructions or contact support for assistance.
      </Text>

      {notFoundAgain && (
        <Box
          style={{
            background: amberBg,
            border: `1px solid ${amberBorder}`,
            borderRadius: 10,
            padding: '10px 12px',
            marginTop: 14,
          }}
        >
          <Text fz={13} c={amberText}>
            Still not detected. If you've just installed the code, clear your cache / CDN and wait a
            couple of minutes — or continue anyway if this page is dynamic or you're confident the SmartCode has been added correctly in your page.
          </Text>
        </Box>
      )}

      <Group spacing={20} mt={16}>
        {/* Platform-specific install guide for this website (Angular, Shopify, WordPress, …) */}
        {guideUrl ? (
          <Anchor href={guideUrl} target="_blank" rel="noreferrer" fz={13.5} fw={600} c={cPurple}>
            <Group spacing={6} noWrap>
              <IconExternalLink size={15} /> View Installation Instructions
            </Group>
          </Anchor>
        ) : (
          <Anchor fz={13.5} fw={600} c={cPurple} onClick={onViewInstructions}>
            <Group spacing={6} noWrap>
              <IconExternalLink size={15} /> View Installation Instructions
            </Group>
          </Anchor>
        )}
      </Group>

      <Group position="apart" mt={24}>
        <Button variant="subtle" color="gray" radius="md" disabled={checking || continuing} onClick={onClose}>
          Cancel
        </Button>
        <Group spacing={10}>
          <Button radius="md" variant="default" loading={continuing} disabled={checking} onClick={handleContinue}>
            Continue Anyway
          </Button>
          <Button
            radius="md"
            color="dark"
            loading={checking}
            disabled={continuing}
            leftIcon={!checking ? <IconRadar2 size={16} /> : undefined}
            onClick={handleRecheck}
          >
            {checking ? 'Re-checking…' : 'Re-check'}
          </Button>
        </Group>
      </Group>
    </Modal>
  );
}
